import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2 } from 'lucide-react';

/**
 * Router page landed on after Stripe checkout.
 *
 * success_url is /post-checkout?session_id={CHECKOUT_SESSION_ID}&tier_id=...
 * We call verify-checkout-session, then redirect to:
 *   - /trial-started (status=trialing)
 *   - /purchase-success (status=active|paid)
 *   - /select-tier (unknown/incomplete)
 *
 * Doesn't fire any conversions itself — the destination pages own those.
 */
const PostCheckout = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setError('Missing session ID. Redirecting back to plan selection…');
      const timer = setTimeout(() => navigate('/select-tier', { replace: true }), 2000);
      return () => clearTimeout(timer);
    }

    let cancelled = false;

    (async () => {
      try {
        const { data, error: fnError } = await supabase.functions.invoke('verify-checkout-session', {
          body: { session_id: sessionId },
        });

        if (cancelled) return;

        if (fnError) {
          logger.error('PostCheckout: verify-checkout-session failed', fnError);
          setError('Could not verify your checkout. Redirecting…');
          setTimeout(() => navigate('/', { replace: true }), 2000);
          return;
        }

        const status = (data?.status as string) ?? 'unknown';
        const query = `?session_id=${encodeURIComponent(sessionId)}`;

        if (status === 'trialing') {
          navigate(`/trial-started${query}`, { replace: true });
        } else if (status === 'active' || status === 'paid') {
          navigate(`/purchase-success${query}`, { replace: true });
        } else {
          logger.warn('PostCheckout: unexpected status', { status });
          navigate('/', { replace: true });
        }
      } catch (err) {
        if (cancelled) return;
        logger.error('PostCheckout: unexpected error', err);
        setError('Something went wrong. Redirecting…');
        setTimeout(() => navigate('/', { replace: true }), 2000);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="flex flex-col items-center gap-4 text-center">
        {error ? (
          <Alert variant="destructive" className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-muted-foreground">Finalizing your checkout…</p>
          </>
        )}
      </div>
    </div>
  );
};

export default PostCheckout;
